import {
  PaymentPlanLineStatus,
  PaymentMode,
  LenderName,
} from "@nemuru-eng/js-api-client";
import enLocale from "date-fns/locale/en-US";
import React from "react";
import { ACTION_EXCEPTIONS } from "../../../utils/getActionException";
import { DefaultTermsAndConditionEn } from "../termsAndConditions/en/defaultTermsAndConditionsEn";
import { INSURANCE_PACK } from "../../../components/modules/card/Card/config";

const en = {
  SIGNING_IN: "Logging in...",
  AUTH_ERROR: "Authentication error",
  WELCOME_BACK: "Welcome back",
  RESUME_LOAN_PROCESS: "Resume your process where you left it.",
  CONTINUE: "Continue",
  // Sections
  SECTION_IDENTIFICATION: "Identification",
  DOCUMENT_ID_VERIFICATION: "$v1 Verification",
  VERIFYING_DOCUMENT: "Verifying document…",
  UPLOADING_DOCUMENT_PICTURE: "Uploading picture…",
  UPLOADING_FRONT_PICTURE: "Uploading front picture…",
  UPLOADING_BACK_PICTURE: "Uploading back picture…",
  LOADING_VERIFICATION_STATUS: "Loading verification status…",
  DOCUMENT_TYPE_SELECTION_INSTRUCTIONS:
    "We'll verify your identity quickly and securely. It will only take a couple of minutes.",
  TRUSTED_ID_VERIFICATION: "Your data is protected in accordance with the GDPR",
  START_VERIFICATION: "Start verification",
  SELECT_YOUR_DOCUMENT_TYPE: "Select your document type",
  INSTRUCTIONS_FOR_DOCUMENT_VERIFICATION: "Instructions",
  ACTION_REQUEST_GET_ID_DOCUMENT_VERIFICATION_STATUS_REJECTED: (
    <div>
      <p>
        We were unable to retrieve the status of your identity document
        verification at this time.
      </p>
      <br></br>
      <span>Please: </span>
      <p>
        <ul>
          <li>Try again by clicking the button below.</li>
          <li>If the error persists, please try again later.</li>
        </ul>
      </p>
    </div>
  ),
  VERIFICATION_FAILED: "Verification failed",
  ID_DOC_VERIFICATION_FAILED_TITLE:
    "We're sorry, we couldn't verify your document.",
  ID_DOC_VERIFICATION_ATTEMPTS: "Attempts: $v1/$v2",
  RESTART_PROCESS: "Restart process",
  ID_DOC_VERIFICATION_LIMITED_ATTEMPTS_TITLE: "Limited attempts",
  ID_DOC_VERIFICATION_LIMITED_ATTEMPTS_BODY:
    "You have a limited number of attempts ($v1 of $v2).",
  ID_DOC_VERIFICATION_REASON_TIMEOUT:
    "The upload session timed out. Please have your document ready and complete the photo process faster on your next attempt.",
  ID_DOC_VERIFICATION_REASON_RECOGNITION:
    "The document could not be recognised, it may be damaged, it may not have been uploaded in the correct orientation, or it may not correspond to the identity document number used to start this application. Use the original document, make sure it is well framed, readable and without glare, and keep it in horizontal position.",
  ID_DOC_VERIFICATION_MAX_ATTEMPTS:
    "You have used all your identity verification attempts. Please restart the process and try again.",
  ID_DOC_UPLOAD_PROCESS_EXPIRED_TITLE: "Session expired",
  ID_DOC_UPLOAD_PROCESS_EXPIRED_BODY:
    "The upload session has expired. Please restart the process to continue.",
  ID_DOC_UPLOAD_INVALID_PHOTO_TITLE: "Invalid $v1 photo",
  front: "front",
  back: "back",
  ID_DOC_UPLOAD_INVALID_PHOTO_BODY:
    "We couldn't validate this image. Please retake the photo and make sure the document is clear and well framed.",
  ID_DOC_UPLOAD_TRANSACTIONAL_ERROR_TITLE: "We couldn't upload your photo",
  ID_DOC_UPLOAD_TRANSACTIONAL_ERROR_BODY:
    "Please try again in a few seconds. If the problem persists, restart the process.",
  ID_DOC_UPLOAD_GENERIC_ERROR_TITLE: "Something went wrong",
  ID_DOC_UPLOAD_GENERIC_ERROR_BODY:
    "We couldn't upload your pictures. Please try again.",
  ID_DOC_UPLOAD_CAPTURED_GENERIC_ERROR_BODY:
    "We couldn't upload the captured image. Please try again.",
  ID_DOC_VERIFY_GENERIC_ERROR_BODY:
    "We couldn't validate your document. Please try again.",
  ID_VERIFICATION_EXTRACTED_DATA_FORM_TITLE: "Identity verification",
  ID_VERIFICATION_EXTRACTED_DATA_FORM_SUBTITLE:
    "Review and complete your identity document details.",
  ID_VERIFICATION_EXTRACTED_DATA_LOADING:
    "Reading the data from your document…",
  ID_VERIFICATION_EXTRACTED_DATA_LOAD_ERROR_TITLE: "We couldn't read your data",
  ID_VERIFICATION_EXTRACTED_DATA_LOAD_ERROR:
    "We couldn't read the data from your document. Please try again.",
  ISSUING_COUNTRY: "Issuing country",
  PROCESS_INFO_BULLET_ORIGINAL: "Use an original document. Do not use a copy.",
  PROCESS_INFO_BULLET_FRAME:
    "Make sure the document is framed inside the frame. It should be readable and focused.",
  PROCESS_INFO_BULLET_HORIZONTAL:
    "The document should be in horizontal position.",
  PROCESS_INFO_BULLET_NOT_EXPIRED: "The document should not be expired.",
  PROCESS_INFO_BULLET_NOT_UNDERAGED:
    "The document holder must be of legal age.",
  PROCESS_INFO_BULLET_GOOD_LIGHTING:
    "Find good lighting and avoid reflections.",
  PHOTO_WILL_BE_UPLOADED_FOR_VERIFICATION:
    "This will be the photo we upload to the system for verification. Make sure the document is framed inside the frame, is readable and is focused.",
  PHOTO_ID_DOCUMENT_front: (
    <>
      Take the <strong>front</strong> photo of your identity document
    </>
  ),
  PHOTO_ID_DOCUMENT_back: (
    <>
      Take the <strong>back</strong> photo of your identity document
    </>
  ),
  STEP_X_OF_Y: "Step $v1 of $v2",
  SECTION_FINANCES: "Finances",
  SECTION_SUMMARY: "Application",
  SECTION_DOCUMENTATION_AND_SIGNATURE: "Documentation and signature",
  SECTION_DOCUMENTATION: "Documentation",
  SECTION_SIGNATURE: "Signature",
  // NotFound
  SORRY: "We are sorry",
  PAGE_NOT_FOUND: "Page not found",
  PROCESS_NOT_ALLOWED_FOR_THIS_LOAN:
    "This process is not allowed for this application",
  THE_IDENTIFICATION_PARAMETERS_FOR_THIS_PROCESS_ARE_NOT_CORRECT:
    "The credentials required for this process are not correct",
  // Disabled
  OOPS: "¡Oooops!",
  LOAN_IS_DISABLED: "This application does not exist anymore",
  // Critical Error Information
  OOPS_SOMETHING_WENT_WRONG: "¡Oooops! Something went wrong",
  PLEASE_RESET_THE_APP:
    "Please reset the application. If the error persists, please contact our customer support service",
  COULD_NOT_FIND_YOUR_BANK_ACCOUNT_NUMBER:
    "We weren't able to find your bank account number. Please contact our customer support service by phone $v1",
  COULD_NOT_FIND_YOUR_BANK_ACCOUNT_NUMBER_CONTACT_CUSTOMER_SUPPORT_CHAT:
    "We weren't able to find your bank account number. Please contact our customer support service by phone $v1 or by chat",

  AUTOCOMPLETE: "Autocomplete",

  // InfoPanel
  WHICH_DOCUMENTS: "Which documents do you need?",
  WELCOME_TO_ID_VERIFICATION_TITLE: "Let’s verify your identity",
  WELCOME_TO_ID_VERIFICATION_BODY:
    "We’ll verify your identity using your ID document. This is a required step to continue with your application.",
  WELCOME_TO_ID_VERIFICATION_REASSURANCE:
    "It usually takes 1–2 minutes, and your photos are used only for verification.",
  DNI_OR_NIE: "Valid DNI or NIE",
  NIE_EU_PASSPORT:
    "In case you have an European NIE without picture, we will request your passport",
  WHAT_DATA_REQUESTED: "Which other data will we request you?",
  ENTRANCE_DATE: "Entrance date in your current residence.",
  OWNERSHIP_CONTRACT:
    "If your residence is of your property, if it is a private contract, public deed or social housing",
  MORTAGE_EXPENSES: "If you have a mortgage, how much do you pay per month.",
  MARITAL_REGIME_TYPES:
    "In case you are married, if the marital regime is of separate property or matrimonial assets.",
  IBAN: "Bank account number (IBAN) for the direct of the loan installments.",
  WHAT_IS_PUBLIC_DEED: "What is public deed?",
  PUBLIC_DEED_EXPLANATION:
    "When a purchase contract is made between the buyer and the seller, collecting the written personal data of both parts and the conditions of the operation (property, type of payment, etc). This contract is taken to a notary who certifies its validity. Moreover, the notary registers the property in the property registration. This is the most common procedure.",
  WHAT_IS_PRIVATE_CONTRACT: "What is a private contract?",
  PRIVATE_CONTRACT_EXPLANATION:
    "When a purchase contract is between the buyer and the seller, collecting the written personal data of both parts and the conditions of the operation (property, type of payment, etc). It is not certified by a notary and it can not be registered in the property registration",
  WHAT_IS_SOCIAL_HOUSING: "What is social housing?",
  SOCIAL_HOUSING_EXPLANATION:
    "When someone acquires a property from the social housing agency.",
  WHAT_IS_MATRIMONIAL_ASSETS: "What is matrimonial assets?",
  MATRIMONIAL_ASSETS_EXPLANATION:
    "The property regime puts in common the assets and profits that are achieved during the marriage. In other words, all the assets obtained during the marriage will belong to both in equal parts, regardless of whether it is the result of the work or income of either of them.",
  WHAT_IS_SEPARATE_PROPERTY: "What is separate property?",
  SEPARATE_PROPERTY_EXPLANATION:
    "In the separation of property regime, both the assets that each person has before getting married and those that they acquire later, will be theirs and not the other person's.",
  HOW_IT_WORKS: "How does it work?",
  HOW_IT_WORKS_INTRO:
    "This process is carried out through Eurobits Technologies (a company that meets the highest security standards certified in ISO 27001, regulated and approved by the Bank of Spain under PSD2 regulations and Decree-Law 19/2018). You only have to:",
  HOW_IT_WORKS_BANK: "Select your bank",
  HOW_IT_WORKS_LOGIN:
    "Login as if it was your online banking. Remember that it is NOT the PIN of your credit/debit card.",
  HOW_IT_WORKS_IBAN:
    "Select the bank account where you want to direct the loan installments.",
  HOW_IT_WORKS_DONE: "Done!",
  WHAT_DATA_IS_READ: "What data will be read?",
  WHAT_DATA_IS_READ_IDENTIFICATION:
    "Identification data (account holders and IBAN)",
  WHAT_DATA_IS_READ_STATEMENTS: "Movement history",
  WHAT_DATA_IS_READ_BALANCE: "Balance",
  WHICH_GOAL: "What will the data be used for??",
  WHICH_GOAL_INCOME:
    "We use the details of your bank account to estimate your ability to pay and to be able to evaluate your request quickly and safely. In addition, we validate that you are the owner of the selected account.",
  WHICH_GOAL_IBAN:
    "Establish the account in which you want to direct the loan installments.",
  DNI_ES_TITLE: "Spanish DNI",
  DNI_ES_EXPLANATION:
    "Documento Nacional de Identidad (DNI) for citizens with the Spanish nationality",
  NIE_UE_TITLE: "NIE for EU citizens (no picture)",
  NIE_UE_EXPLANATION:
    "Número de Identidad Extranjero (NIE) for European Union citizens. The document does not have a picture",
  NIE_NON_UE_TITLE: "NIE for non EU citizens (with picture)",
  NIE_NON_UE_EXPLANATION:
    "It is the Tarjeta de Identidad de Extranjero (TIE) for non European Union citizens. The document has picture.",
  //Welcome Wizard
  LETS_GO: "Hello again",
  YOUR_LOAN_PROCESS: "This is how the process is:",
  IDENTIFICATION: "Identification",
  ID_CHECK: "ID verification",
  PROFESSIONAL_AND_FINANCIAL_DATA: "Financial and professional data",
  SUMMARY: "Summary",
  LOAN_ADJUSTMENT_AND_EVALUATION: "Loan adjustment and final evaluation",
  PROPOSAL_DETAILS: "Proposal sent by $v1 for an amount of $v2€.",
  PROPOSAL_DETAILS_WITHOUT_AMOUNT: "Proposal sent by $v1",
  DOCUMENTATION_AND_SIGNATURE: "Documentation and signature",
  SIGNATURE: "Signature",
  UPLOAD_DOCUMENTATION_AND_SIGN: "Upload documentation and digital signature.",
  // Slider
  HI: "Hi",
  START: "Start",
  SKIP: "Skip",
  DESCRIPTION: "Description",
  // Step 1
  WELCOME: "Welcome",
  WELCOME_TO_NEMURU: "Welcome to Nemuru",
  INVITATION_MSG:
    "$v1 invited you to start the process for installments payment.",
  INVITATION_MSG_CARD:
    "$v1 invited you to start the process of contracting your credit card.",
  NEUTRAL_INVITATION_MSG:
    "You have been invited to start the process for installments payment.",
  GET_YOUR_LOAN: "From here, you can request your loan.",
  // Step 2
  WHAT_IS_NEMURU: "What is Nemuru?",
  YOU_SHOULD_KNOW: "You should know that...",
  NEMURU_INTRO:
    "Nemuru is the technology platform that allows you to apply for your financing in 3 simple steps.",
  NEMURU_INTRO_SUB:
    "We are not a financier, but we work with the main financial entities.",
  // Step 3
  WHAT_WILL_YOU_NEED: "What will you need?",
  DOCUMENT_RECOMMENDATION:
    "Before you begin, please consider the following points:",
  DNI_OR_NIE_DOC: "Valid DNI or NIE.",
  WE_WILL_REQUEST_INFORMATION: "we will request you information",
  ABOUT_YOUR_FINANCES:
    "about your finances and professional data, necessary to process your financing application",
  ALL_INFO_PRIVATE: "All information will be private and strictly protected",
  SPANISH_ACCOUNT: "Spanish bank account.",
  INCOME: "Income",
  SPANISH_ACCOUNT_DETAIL:
    "You must have a Spanish bank account where you can directly debit the loan installments.",
  OTHER_DOCUMENTS: "Other documents",
  OTHER_DOCUMENTS_DETAIL:
    "If you are not yet a CaixaBank customer, we will ask you to provide a proof of income (like a payroll, tax return, etc.) and a proof of ownership of the bank account you have given us to direct your installments.",
  // Step 5
  DNI_OR_NIE_VALID: "Valid DNI, NIE, or Passport",
  DNI_NIE_OR_PASSPORT_DOC:
    "If you do not have a valid European DNI or NIE, we will ask for a photo of your passport.",
  EUROPEAN_BANK_ACCOUNT: "European Bank Account",
  SEPA_PAYMENT_DETAILS:
    "You must have a bank account within the Single Euro Payments Area (SEPA) to set up the loan installments. If you do not have a European bank account, we will offer an alternative payment method through Flywire.",
  SEPA_BANK_ACCOUNT_TITLE: "Single Euro Payments Area (SEPA)",
  SEPA_BANK_ACCOUNT_EXPLANATION:
    "The SEPA area consists of thirty-six countries: the members of the European Economic Area (comprising the twenty-seven European Union states, plus Liechtenstein, Iceland, and Norway) and Andorra, Monaco, San Marino, Switzerland, the United Kingdom, and Vatican City State.",
  // GDPR
  TERMS_AND_CONDITIONS: DefaultTermsAndConditionEn,
  ACCEPT: "Accept",
  CANCEL: "Cancel",
  PLEASE_ACCEPT_CONDITIONS: "It is necessary to accept this condition.",
  REQUIRED: "(Required)",
  PROVIDE_CONDITIONS_TO_CLIENT:
    "Please provide the terms and conditions to your client.",
  UPF_GDPR_AND_PRIVACY_POLICY_OPTIN: (
    <p>
      Nemuru will share your data and credit report with Fundació Institut
      d’Educació Continua, which will process the data in accordance with the
      terms and conditions established in their general terms and their privacy
      policy that you have previously accepted and can find{" "}
      <a
        href="https://www.bsm.upf.edu/documents/general-conditions-eng-20231106.pdf"
        target="_blank"
      >
        here
      </a>
      .
    </p>
  ),
  // FLOW MESSAGES
  END_SECTION_IDENTIFICATION: "Identity confirmed",
  END_SECTION_FINANCES: "Financial data entered correctly",
  CONGRATULATIONS: "Congratulations $v1!",
  THANKS: "Thank you $v1!",
  CONGRATULATIONS_SIGNATURE_NOT_AVAILABLE: "Congratulations $v1!",
  CONGRATULATION_DOCUMENTS_UPLOADED: "Congratulations $v1!",
  CONGRATULATIONS_APPROVED_END: "Congratulations $v1!",
  WE_ARE_SORRY: "We are sorry $v1",
  THANKS_LOAN_FORM_COMPLETED: "You have completed the process successfully.",
  THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED_DOCS_AND_SIGNATURE_REDIRECT:
    "Your request has been preaproved and is pending validation!",
  THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED:
    "Your request has been preaproved and is pending validation!",
  APPLICATION_PENDING_APPROVAL: "Your application is pending approval",
  LENDER_SELECTED_PENDING_APPROVAL:
    "Your request is pending approval by the selected financial institution. You will receive an email to continue with the process in case of approval.",
  THANKS_DOCUMENTATION_UPLOADED: "Thank you for uploading the documents.",
  LOAN_PREAPPROVED_DOCUMENTATION_NEEDED:
    "The request is subject to validation by the financial institution. Upload documentation now to get final confirmation faster!",
  LOAN_HAS_BEEN_DENIED: "Your application has been denied.",
  YOUR_COMMERCIAL_AGENT_WILL_CONTACT_YOU_SOON_FOR_MORE_DETAILS: (
    <p>
      We have conducted the financing request study. Your{" "}
      <strong>sales agent</strong> will contact you shortly to inform you of the
      next steps.
    </p>
  ),
  LOAN_ONBOARDINGS_ARE_NOT_COMPLETED: "$v1 has/have to fill in the details:",
  LOAN_ANALYSING:
    "We have launched your request to financial institutions and we will answer you soon. Stay tuned!",
  LOAN_ANALYSING_DOCUMENTATION_NEEDED:
    "We have launched your request to financial institutions. Upload the documentation now to get the answer faster!",
  DOWN_PAYMENT_PENDING: "Pay the down payment",
  INVALID_DOWN_PAYMENT_CHECKOUT_SESSION_FOR_SIGNATURE:
    "Your down payment is pending",
  YOU_MUST_PAY_THE_DOWN_PAYMENT_BEFORE_SIGNING_THE_CONTRACT:
    "Complete the down payment using the payment method that suits you best.",
  CLOSE_THE_DIALOG_AND_PAY_THE_DOWN_PAYMENT: (
    <>
      <>You must make the down payment before signing the contract.</>
      <p>
        Close this popup window and proceed to make the down payment by
        selecting that option on the payment methods page.
      </p>
    </>
  ),

  APPROVED: "Your application has been preaproved!",
  IN_CASE_OF_APPROVAL: "In case of approval",
  WAITING_FOR_REMAINING_ACTIONS:
    "One moment, we're checking if there's any pending action...",
  TWO_ACTIONS_REMAINING: "Complete the next 2 steps",
  THREE_ACTIONS_REMAINING: "Complete the next 3 steps",
  ONE_ACTION_REMAINING: "Complete the next step",
  NO_ACTION_REMAINING: "All steps completed",
  DOCS_COMPLETED: "Documentation uploaded",
  DOCS_PENDING: "Upload your documentation",
  DOCS_VALIDATION_PENDING: "Validate documentation",
  DOCS_VALIDATION_PENDING_INFO:
    "Go to the IKEA financing counter and show the QR code you will receive by email to an agent to complete the validation of your documentation.",
  ONBOARDING_COMPLETED: "Data correctly filled",
  PENDING_ONBOARDING: "Pending filling data",
  PENDING_ONBOARDING_INFO:
    "Once all the participants in the application have completed their data, you will be able to upload the documentation and sign the contract digitally.",
  DOCS_PENDING_INFO: "Upload the documents we will request below.",
  SIGNATURE_COMPLETED: "Contract signed",
  SIGNATURE_PENDING: "Sign the contract",
  SIGNATURE_PENDING_INFO: "Review the contract and sign it digitally.",
  SIGNATURE_PENDING_BY_MAIL_INFO:
    "You will receive the contract to sign it digitally.",
  SIGNATURE_PENDING_BY_MAIL_INFO_MESSAGE:
    "Check your email and sign from any device.",
  SIGNATURE_PENDING_BY_APP_INFO: "You can sign the contract in the next step.",
  SIGNATURE_MODULE_YET_NOT_AVAILABLE: (
    <>
      <p>The digital signature process is not yet available.</p>
      <p>
        We will inform you by email as soon as it is possible to sign the
        contract digitally.
      </p>
    </>
  ),
  YOU_CAN_SAFELY_CLOSE_THIS_WINDOW: (
    <p>
      <b>You will soon receive an answer in your email.</b>
      <br /> You can now safely close this window.
    </p>
  ),
  RECEIVE_CONTRACT_TO_SIGN:
    "You will receive the contract to sign it digitally from any device.",
  INFORMATION_IN_CASE_OF_APPROVAL:
    "In the event that the financial institution needs more additional documentation, we will inform you.",
  SEND_DOCUMENTATION_NEEDED:
    "You need to provide the required documentation. Do it now!",
  UPLOAD_DOCS_NOW: "Upload documentation now",
  RETURN_TO_MERCHANT: "Return to merchant",
  REDIRECTING_TO_MERCHANT: "Redirecting to merchant",
  SIGN_NOW: "Proceed to signature",
  SYSTEM_FAILURE_DETECTION:
    "Your profile does not adapt to the risk policies of the collaborating financial entities.",
  DOCUMENTATION_UPLOADED_SUBTITLE:
    "You have uploaded all the documentation correctly.",
  DOCUMENTATION_UPLOADED_DETAIL:
    "Stay tuned! We will keep you informed by email.",
  PLEASE_WAIT: "Please wait a moment",
  INFO_SYNC:
    "Please wait while we update the status. This process can take a few seconds.",
  // SummaryView
  LETS_START: "Let's go",
  ID: "Identification",
  ID_VERIFICATION: "Identity verification",
  FINANCES: "Finances",
  FINANCE_PROFESSIONAL_INFO: "Financial and professional data",
  LOAN_ADJUSTMENT: "Adjustment and evaluation of your loan",
  THIS_IS_THE_PROCESS: "This will be the application process for your loan",
  CHECK_ID: "Identity check",
  CHECK_ID_AGAIN: "We need to run your automatic identity check again",
  RESUME_PROCESS:
    "You can resume the application process from where you left off",
  RESUME: "Resume",
  RETRY: "Retry",
  FROM: "From",
  FROM_LOWERCASE: "from",

  // KycAutomatic
  VIDEO_ID: "Video identification",
  SELECT_DOCUMENT_TYPE: "Select the type of document",
  REMEMBER_TO_ACTIVATE_PERMISSIONS:
    "Remember to enable camera and microphone permissions on your device. Have your ID with you in a well-lit environment.",
  START_ID: "Start identification",
  FOLLOW_INSTRUCTIONS: "Follow the instructions",
  CONSTENT_VIDEO_ID:
    "I accept that the video-identification is carried out and biometric and indicative data is collected in order to certify my identity. For more information see",
  MANUAL_IDENTIFICATION: "Manual identification",
  NO_ELECTRONIC_ID_FOR_PASSPORT_MESSAGE:
    "Next you must introduce your personal information and upload a picture of your passport",
  WELL_DONE: "Very well!",
  YOUR_VIDEO_ID_HAS_BEEN_SUCCESSFULLY_RECORDED:
    "You have successfully completed the video identification process.",
  YOU_CAN_SAFELY_CLOSE_THIS_TAB: "You can safely close this tab now.",
  AN_ERROR_OCURRED_DURING_THE_VIDEO_ID:
    "An error occurred during the video identification process.",
  PLEASE_TRY_AGAIN: "Please try again by clicking the button below.",
  REPEAT_VIDEO_ID: "Repeat video identification",
  SUNDAY: "Sunday",
  FRIDAY: "Friday",
  VIDEO_ID_ASSISTANCE_MESSAGE:
    "If you are having difficulties with the video identification call, you can call us at $v1 from Monday to $v2 from $v3 to $v4 to help you and guide you through the process. We apologize for the inconvenience and thank you for your patience.",

  // ElectronicId
  ELECTRONIC_ID_ERROR_TITLE: "A problem ocurred",
  ELECTRONIC_ID_HTTPS_REQUIRED:
    'Please check that the address (URL) from which you are accessing begins with "https://..."',
  ELECTRONIC_ID_IMAGE_BLURRED:
    "Clean the lens of the camera or try from another device with higher image quality.",
  ELECTRONIC_ID_AUTHORIZATION_INVALID:
    "Authorization identification is not valid",
  ELECTRONIC_ID_MAX_LENGTH_EXCEEDED:
    "Try to improve the recording environment (lighted place, no shadows, have the original document at hand). You can also try from another device with higher image quality.",
  ELECTRONIC_ID_DOCUMENT_TAMPERED:
    "Remember that you must use the original document and in good condition. You cannot use a photocopy.",
  ELECTRONIC_ID_ERROR:
    "Try to improve the recording environment (lighted place, no shadows, have the original document at hand). You can also try from another device with higher image quality. Remember that you must use the original document and in good condition. You cannot use a photocopy.",
  ELECTRONIC_ID_LUMINANCE_TOO_HIGH:
    "Try from another place with different lighting and no shadows. You can also try it from another device with better image quality.",
  ELECTRONIC_ID_LUMINANCE_TOO_LOW:
    "It seems that there is not enough light. Please find a better lit place. You can also try it from another device with better image quality.",
  ELECTRONIC_ID_USER_ABORTED: "Please try again.",
  ELECTRONIC_ID_INACTIVE_USER_ABORTED: "Please try again.",
  ELECTRONIC_ID_TYPE_INVALID:
    "Remember that you must use the original document and in good condition. You cannot use a photocopy.",
  ELECTRONIC_ID_DOCUMENT_SIDES_MISMATCH:
    "Make sure that you use a single document of which you are the owner.",
  ELECTRONIC_MICROPHONE_NOT_FOUND:
    "You must activate the permission of the microphone from the Settings of your device or from the browser settings. You can also try it from another device.",
  ELECTRONIC_ID_MAX_FPS_EXCEEDED:
    "Please try from another device/browser. Make sure you are connected to a network with a good connection and are using a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_CAMERA_INVALID_REQUIREMENTS:
    "Try it from another device with another model of camera. Please try from another device/browser. Make sure you are connected to a network with a good connection and are using a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_DOCUMENT_GLARED:
    "Try again avoiding reflections on the document. Remember that you must use the original document and in good condition. You cannot use a photocopy.",
  ELECTRONIC_ID_DEVICE_NOT_SUPPORTED: "Please try from another device.",
  ELECTRONIC_ID_PERMISSIONS_REFUSED: "Please try from another device.",
  ELECTRONIC_ID_FACES_MISMATCH:
    "The person holding the document must be the one who carries out the identification process.",
  ELECTRONIC_ID_MULTIPLE_FACES:
    "Make sure that only the holder of the document is present during the identification.",
  ELECTRONIC_ID_CAMERA_RESOLUTION_TOO_LOW:
    "Try from another device with higher resolution.",
  ELECTRONIC_ID_CONNECTION_INTERRUPTED:
    "Connection issues. Try from another location or by connecting to another network.",
  ELECTRONIC_ID_CONNECTION_ERROR:
    "Try from another location or by connecting to another network. Use a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_VIDEO_ID_REJECTED: "",
  ELECTRONIC_ID_DOCUMENT_NOT_ALLOWED:
    "Remember that you must use the original document and in good condition. You cannot use a photocopy.",
  ELECTRONIC_ID_CAMERA_NOT_FOUND:
    "You must activate the permission of the camera from the Settings of your device or from the browser settings.",
  ELECTRONIC_ID_ACCESS_TOKEN_INVALID: "",
  ELECTRONIC_ID_AGENT_ABORTED: "",
  ELECTRONIC_ID_VIDEO_ID_NOT_SUPPORTED:
    "Try from another browser. Recommended browsers: Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_BARCODE_ILLEGIBLE:
    "Clean the lens of the camera or try from another device with higher image quality.",
  ELECTRONIC_ID_RTC_CONNECTION_NOT_AVAILABLE:
    "Try it from another device/browser. Make sure you are connected to a network with a good connection and are using a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_BANDWITH_TOO_LOW:
    "Try from another location or by connecting to another network. Use a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_HOLOGRAM_NOT_DETECTED: "",
  ELECTRONIC_ID_CONNECTION_UNSTABLE:
    "Try from another location or by connecting to another network. Use a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_CAPTCHA_INVALID:
    "Try from another browser. Recommended browsers: Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_AUTHORIZATION_REUSED: "",
  ELECTRONIC_ID_VIDEO_ID_BAD_REQUEST:
    "If the problem persists, try from another location or by connecting to another network. Use a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_RTC_CONNECTION_LOST:
    "Try from another location or by connecting to another network. Use a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_BARCODE_NOT_DETECTED:
    "Remember that you must use the original document and in good condition. You cannot use a photocopy. You can also try it from another device with better image quality.",
  ELECTRONIC_ID_FACE_SCAN_DISCONTINUITY:
    "Make sure that the process is carried out continuously by the owner of the document. Remember that you must use the original document and in good condition. You cannot use a photocopy.",
  ELECTRONIC_ID_USER_UNDERAGE:
    "The coming of age is mandatory to be able to continue with the process.",
  ELECTRONIC_ID_CAMERA_UNAVAILABLE:
    "Make sure the camera is not being used by another app. Please close other apps you have open on your device.",
  ELECTRONIC_ID_DOCUMENT_EXPIRED: "You must use a non expired valid document.",
  ELECTRONIC_ID_MICROPHONE_PERMISION_DENIED:
    "You must activate the permission of the microphone from the Settings of your device or from the browser settings.",
  ELECTRONIC_ID_DOCUMENT_WRONG_SIDE:
    "Repeat the process following the directions. Remember that you must show the front of the document first and then the back.",
  ELECTRONIC_ID_SMILE_ID_NOT_VALIDATED:
    "Make sure that the process is carried out continuously by the person who owns the document.",
  ELECTRONIC_ID_TYPE_UNAUTHORIZED:
    "Remember that you must use the original document and in good condition. You cannot use a photocopy. The owner must be of legal age and the document must be current.",
  ELECTRONIC_ID_CONNECTION_LOST:
    "Try from another location or by connecting to another network. Use a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_SURFACE_NOT_REFLECTIVE:
    "Remember that you must use the original document and in good condition. You cannot use a photocopy. The system must be able to verify all the security elements of the document.",
  ELECTRONIC_ID_NO_AGENT_AVAILABLE: "",
  ELECTRONIC_ID_PERMISSION_DENIED:
    "You must activate the microphone and camera permissions from the Settings of your device or from the browser settings.",
  ELECTRONIC_ID_BROWSER_NOT_SUPPORTED:
    "Try from another browser. Recommended browsers: Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_DOCUMENT_ILLEGIBLE:
    "Clean the camera lens or try from another device. Remember that you must use the original document and in good condition. You cannot use a photocopy.",
  ELECTRONIC_ID_USER_INACTIVE: "",
  ELECTRONIC_ID_TYPE_NOT_SUPPORTED_IN_WEB_BROWSER:
    "Try from another browser. Recommended browsers: Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_RECORDING_ERROR:
    "Please try again. Remember that you must have an internet connection and be in a well lit place. Use a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_FACE_SPOOFING:
    "Make sure that the process is carried out continuously by the owner of the document. Remember that you must use the original document and in good condition. You cannot use a photocopy. The system must be able to verify all the security elements of the document.",
  ELECTRONIC_ID_DOCUMENT_SIDES_MATCH_NOT_POSSIBLE:
    "Make sure that you use a single document of which you are the owner. Remember that you must use the original document and in good condition. You cannot use a photocopy.",
  ELECTRONIC_ID_RTC_CONNECTION_ERROR:
    "Try it from another device/browser. Make sure you are connected to a network with a good connection and are using a recommended browser (Google Chrome, Mozilla Firefox, Microsoft Edge and Safari (iOS).",
  ELECTRONIC_ID_CAMERA_PERMISSION_DENIED:
    "You must activate the permission of the camera from the Settings of your device or from the browser settings.",
  ELECTRONIC_ID_LIVENESS_NOT_DETECTED: "",
  // KycManual
  DNI_ES: "Spanish DNI",
  NIE_UE: "NIE within EU (no picture)",
  NIE_NON_UE: "NIE outside EU (with picture)",
  PASSPORT: "Passport",
  SELECT_YOUR_ID: "Select your ID",
  DOCUMENT_NUMBER: "Document number",
  INVALID_DOCUMENT_NUMBER: "Invalid document number",
  EXPIRATION: "Expiration",
  CONTRACT_EXPIRATION: "Current work contract expiration",
  DOCUMENT_EXPEDITOR: "Document expeditor",
  NATIONALITY: "Nationality",
  ID_INFORMATION: "Identity information",
  DOCUMENT_TYPE: "Document type",
  NAME_SURNAME: "Name and surname",
  PERSONAL_INFORMATION: "Personal information",
  BASIC_ID_INFORMATION: "Basic identity information",
  FIRST_NAME: "Name",
  LAST_NAME: "Surname",
  GENDER: "Gender",
  MALE: "Male",
  FEMALE: "Female",
  BIRTH_DATA: "Birth data",
  BIRTHDATE: "Birthdate",
  BIRTH_CITY: "Birth city",
  BIRTH_PROVINCE: "Birth province",
  BIRTH_INFO: "Birthplace and birthdate",
  TAKE_PICTURE: "Take picture",
  PREPARING_CAMERA: "Preparing camera…",
  ID_DOC_CAMERA_ERROR_TITLE: "Camera not available",
  ID_DOC_CAMERA_ERROR_BODY:
    "We couldn't access your camera. Please allow camera permissions in your browser/device settings, or choose Upload pictures to continue.",
  SELECT_FROM_GALLERY: "or select from gallery",
  IMAGE_ID: "Id document image",
  DNI_ES_FRONT: "DNI front picture",
  DNI_ES_BACK: "DNI back picture",
  NIE_UE_FRONT: "NIE front picture",
  NIE_UE_BACK: "NIE back picture",
  NIE_NON_UE_FRONT: "NIE front picture",
  NIE_NON_UE_BACK: "NIE back picture",
  PICTURE_INSTRUCTIONS_MOBILE:
    "Please try to take a close-up, centered, and uncropped photo of the document. We recommend a well-lit environment so that the data is readable.",
  PICTURE_INSTRUCTIONS:
    "Please upload a close-up, centered, and uncropped photo or image of the document.",
  INVALID_DATE: "Invalid date",
  PAST_DATE_NOT_ALLOWED: "Past date not allowed",
  FUTURE_DATE_NOT_ALLOWED: "Future date not allowed",
  UNDERAGE_NOT_ALLOWED: "You must be of legal age",
  LAST_NAME_TWO_WORDS_REQUIRED: "Please enter both surnames",
  DOCUMENT_TYPE_SHORT_NIF: "DNI",
  DOCUMENT_TYPE_SHORT_NIE_EU: "NIE (EU)",
  DOCUMENT_TYPE_SHORT_NIE_NON_EU: "NIE",
  KYC_VIDEO_VERIFICATION_CONTEXT:
    "To confirm that you are the holder of the document you previously verified, we will carry out a short video identification.",
  UserDetailsDocumentNumberDoesNotMatchKycException:
    "The document recorded in the video does not match the data from the form. Please repeat the process with the correct document.",
  VIDEO_ID_PENDING_INFO: "Verify the authenticity of your identity document",
  // AdditionalData
  IMAGE_ID_NIE_EU: "Passport image",
  PASSPORT_FRONT: "Passport bio page photo",
  PASSPORT_PICTURE_INSTRUCTIONS_MOBILE:
    "Please try to take a close-up, centered, and uncropped photo of the passport bio page.",
  PASSPORT_PICTURE_INSTRUCTIONS:
    "Please upload a close-up, centered, and uncropped photo or image of the passport bio page.",
  UPLOAD_PICTURE: "Upload picture",
  UPLOAD_PICTURES: "Upload pictures from device",
  UPLOAD_PICTURES_ACTION: "Upload pictures",
  LOAD_PICTURE: "Load picture",
  UPLOAD_PICTURE_FORMAT_HINT: "Only JPG or PNG images are allowed.",
  UPLOAD_PICTURE_FORMAT_ERROR:
    "Invalid format. Please upload a JPG or PNG image.",

  ADDITIONAL_INFORMATION: "Additional information",
  CURRENT_RESIDENCE_ADDRESS: "Current residence address",
  FAMILY_SITUATION: "Family situation",
  ADDRESS: "Address",
  ADDRESS_FLOOR: "Floor",
  ADDRESS_DOOR: "Door",
  FLOOR_NUMBER: "Floor $v1",
  DOOR_NUMBER: "Door $v1",
  ADDRESS_DIALOG_TITLE: "Current residence address",
  CONFIRM_ADDRESS: "Confirm address",
  EDIT_ADDRESS: "Edit address",
  ADDRESS_DIALOG_ERROR_TITLE: "Select an option",
  ADDRESS_CONTINUE_MESSAGE:
    "Write your address and select one of the available options in the dropdown list.",
  ADDRESS_DIALOG_ERROR_MESSAGE:
    "We couldn't geolocate your address. Please check that it is correct by editing the field and selecting an option.",
  ADDRESS_DIALOG_SPANISH_ADDRESS_REMINDER:
    "Remember that your address must be located in Spanish territory.",
  ADDRESS_DIALOG_WARNING_MESSAGE:
    "It seems that this address is missing a street number. Please verify that it is correct.",
  ADDRESS_DIALOG_SUCCESS_MESSAGE: (
    <div>
      The address seems valid.
      <br />
      Click on <strong>Confirm address</strong> if it is correct.
    </div>
  ),
  IN_SPAIN: "(street and number, within Spain)",
  STREET_AND_NUMBER: "(street and number)",
  SPAIN: "Spain",
  ADDRESS_ERROR_MISSING_STREET_NUMBER:
    "Please enter the street number in the address.",
  ADDRESS_ERROR_MISSING_POSTAL_CODE:
    "We couldn't detect the postal code. Please try adjusting the entered address.",
  ADDRESS_ERROR_MISSING_GENERAL_ERROR:
    "The address does not seem valid. Please try adjusting the entered address.",

  // AUTOCOMPLETE ADDRESS
  UPDATING_FIELDS: "Updating fields...",
  LOADING: "Loading...",
  COMPLETE_ADDRESS_MANUALLY: "Complete address manually",
  STREET_NAME: "Street name",
  STREET_NUMBER: "Street number",
  FLOOR: "Floor",
  DOOR: "Door",
  POSTAL_CODE: "Postal code",
  INVALID_POSTAL_CODE: "Invalid postal code",
  PROVINCE: "Province",
  COUNTRY: "Country",
  CITY: "City",
  REGION: "Region",
  LATITUDE: "Latitude",
  LONGITUDE: "Longitude",
  BUILDING_LABEL: "Building",
  NO_OPTIONS_TO_DISPLAY: "No options available",
  FIELD_VALUE_MUST_BE_BELOW_X_CHAR: "Field value cannot exceed $v1 characters",
  FIELD_VALUE_MUST_NOT_CONTAIN_SPACES: "Field value cannot contain spaces",
  SEARCH_RESULTS: "Autocomplete results:",
  SELECTED_ADDRESS: "Selected address",
  FILL_ADDRESS_VALUES: "Fill in address details:",
  EDIT_ADDRESS_VALUES: "Edit address details:",
  EDIT: "Edit",
  SEARCH_AGAIN: "Search again",
  CONFIRM_DATA: "Confirm data",
  ADDRESS_AUTOCOMPLETE: "Autocomplete address",
  ADDITIONAL_ADDRESS: "Additional address details",

  HOME_INFO: "Current address information",
  MY_CURRENT_ADDRESS_IS: "My current address is",
  OWNERSHIP: "Owned",
  RENT: "Rented",
  LEASING: "Leased by company",
  PARTNER: "Spouse's property",
  PARENTS: "Parents' property",
  USUFRUCT: "Usufruct",
  OTHER: "Other",
  HOUSE: "House",
  APARTMENT: "Apartment",
  ADDRESS_START_DATE: "Date of entry into current address",
  HOME_EXPENSES_AMOUNT_RENT: "€/month rent",
  PROPERTY_TYPE: "Property type",
  MORTGAGE: "With mortgage",
  HOME_EXPENSES_AMOUNT: "€/month mortgage",
  YOUR_PART: "(your proportional part)",
  MORTGAGE_AMOUNT_GREATER_THAN_VALUE:
    "The mortgage amount should be greater than $v1 €",
  RENT_AMOUNT_GREATER_THAN_VALUE:
    "The rent amount should be greater than $v1 €",
  PRIVATE_CONTRACT: "With private contract",
  PUBLIC_DEED: "With public deed",
  SOCIAL_HOUSING: "Social housing",
  OTHERS: "Others",
  MARITAL_STATUS: "Marital status",
  MARITAL_REGIME: "Marital regime",
  MARRIED: "Married",
  SEPARATED: "Separated",
  SINGLE: "Single",
  WIDOWED: "Widowed",
  DIVORCED: "Divorced",
  SEPARATED_IN_FACT: "Separated in fact",
  SIMILAR_SITUATION_TO_MARRIED: "Similar situation to married",
  MATRIMONIAL_ASSETS: "With joint ownership regime",
  SEPARATE_PROPERTY: "With separate property regime",
  CHILD_SUPPORT_NUMBER: "Number of children in charge",
  CUPS_SUPPLY_CODE: "CUPS supply code",
  CUPS_CODE: "CUPS code",
  WHAT_IS_CUPS: "What is the CUPS code?",
  HOW_TO_FIND_CUPS: "How to find my CUPS code?",
  CUPS_CODE_EXPLANATION: (
    <div>
      <p>
        The Universal Supply Point Code, also known as CUPS, is the code that
        identifies each supply point of the natural gas and electricity network
        in Spain.
      </p>
      <p>
        The main difference between the CUPS code for electricity and natural
        gas is that the electricity code starts with ES00 and the natural gas
        code starts with ES02, although it may vary depending on the
        distributor.
      </p>
    </div>
  ),
  HOW_TO_FIND_CUPS_EXPLANATION_1: (
    <div>
      <strong>On the gas or electricity bill:</strong>
      <span>&nbsp;</span>
      the CUPS code of the installation is included on all bills and this code
      does not change.
    </div>
  ),
  HOW_TO_FIND_CUPS_EXPLANATION_2: (
    <div>
      <strong>By calling the distributor:</strong>
      <span>&nbsp;</span>
      if you do not have a previous bill, you can obtain it through the
      distributor in your area, providing the exact address of the supply point
      and the personal data of the holder.
    </div>
  ),
  INVALID_CUPS_NUMBER: "Invalid CUPS code number",
  DOCUMENT_NUMBER_VALIDATION_FAIL:
    "The proposal data sent by the merchant do not match the video identification.",
  PLEASE: "Please",
  RESEND_PROPOSAL: "Talk to your merchant to send a new request",
  RETRY_VIDEO_ID:
    "Perform a new video identification with the data provided by the merchant",
  // Eurobits
  FINANCIAL_DATA: "Financial data",
  CONNECT_WITH_YOUR_BANK: "Connect with your bank",
  UPLOAD_DOCUMENTATION: "Upload documentation",
  START_CONNECTION_WITH_YOUR_BANK: "Connect with your bank",
  GET_INFO_BANK:
    "We obtain the information we need by connecting with your bank.",
  EUROBITS_INFO:
    "This process is carried out through Eurobits Technologies and is completely private and confidential. ",
  CONNECT_BANKING_INFO:
    "Connect with your bank with the usual access to your online banking",
  ONLY_WHAT_WE_NEED: "Only what we need",
  EUROBITS_INFO_PHRASE1: "Only your financial capacity is analyzed",
  EUROBITS_INFO_PHRASE2: "No charge will be made to your bank account",
  ONLINE_BANK: "Without paperwork",
  TRADITIONAL: "With paperwork",
  PAPERLESS_EASY_AND_FAST: "Paperless, easy and fast",
  PAPERLESS: "Paperless",
  EASY_AND_FAST: "Easy and fast",
  TOTALLY_SAFE: "Totally safe",
  SELECT_BANK_AND_LOGIN:
    "Select and log in to your online banking. Remember that it is not your card's PIN.",
  NO_PASSWORD_SAVED: "At no time do we have access to or save your password.",
  NO_PIN: "Remember that it is not the card's PIN",
  TRADITIONAL_PATH:
    "I prefer to declare my income through the traditional route",
  LONGER_PROCESS:
    "This process will take a little longer and you will have to wait to know the status of the request",
  DOCUMENTATION_NEEDED:
    "You will have to provide documents that reflect the declared income as well as proof of bank ownership",
  //errors
  ERROR_RNCP_TITLE: (
    <div>We're sorry, we couldn't find any valid payment account.</div>
  ),
  ERROR_RNCP_TEXT:
    "Please, try the banking connection process again, making sure to provide a valid payment account.",
  ERROR_RIDN_TITLE: (
    <div>
      We're sorry, the account holder provided differs from the loan recipient.
    </div>
  ),
  ERROR_RIDN_TEXT:
    "Please, try the banking connection process again, ensuring that you provide an account with the same holder as the loan recipient.",
  ERROR_RECI_TITLE: (
    <div>We're sorry, we cannot verify the ownership of this account.</div>
  ),
  ERROR_RECI_TEXT:
    "Unfortunately, we cannot formalize the loan with the provided data. If you wish, you can try again with a different banking connection.",
  ERROR_BR_DEFAULT_TITLE: "We're sorry, an error has occurred in the process.",
  ERROR_BR_DEFAULT_TEXT: "Please, try the banking connection process again.",
  // CashInCashOutMethod
  SELECT_YOUR_BANK_ACCOUNT: "Select your direct debit bank account",
  // Professional Data
  PROFESSIONAL_DATA: "Professional Data",
  EMPLOYMENT_STATUS: "Employment Status",
  PROFESSIONAL_POSITION: "Professional Position",
  COMPANY_INFO: "Company Information",
  COMPANY_ACTIVITY: "Company Activity",
  COMPANY_CITY: "Company City",
  COMPANY_NAME: "Company Name",
  COMPANY_YEAR: "Starting Date of Current Job",
  INDEFINITE_EMPLOYED: "Indefinite Contract",
  TEMPORARY_EMPLOYED: "Temporary Contract",
  FIXED_DISCONTINUOUS_EMPLOYED: "Fixed Discontinuous Contract",
  CIVIL_SERVANT: "Civil Servant",
  SELF_EMPLOYED: "Self-Employed",
  TRAINEE: "Trainee / Intern",
  CASUALTY_WORKER: "Seasonal Worker",
  UNEMPLOYED: "Unemployed",
  RETIRED: "Retired",
  PENSIONER: "Pensioner",
  STUDENT: "Student",
  RENTIER: "Rentier",
  WIDOWS_INCOME: "Widow's Income",
  OTHER_PROFESSION: "Homemaker",
  ADMINISTRATIVE: "Administrative",
  MANAGING_DIRECTOR: "Managing Director",
  MANAGER: "Manager",
  COMPANY_OWNER: "Company Owner / Administrator",
  TECHNICIANS_AND_PROFESSIONAL: "Technician or Professional",
  SHOP_ASSISTANT: "Sales Assistant / Waiter",
  COMMERCIAL_AND_SALES_PERSON: "Salesperson",
  LABOR_WORKER: "Laborer",
  ENGINEER: "Engineer",
  ARCHITECT: "Architect",
  LAWYER_OR_NOTARY_ATTORNEY: "Lawyer or Notary Public",
  DOCTOR_OR_DENTIST: "Healthcare Professional",
  PHARMACEUTICAL: "Pharmacist",
  VETERINARY: "Veterinarian",
  TEACHER: "Teacher",
  JOURNALIST: "Journalist",
  FARMER_OR_RANCHER: "Farmer or Rancher",
  TRANSPORTER: "Transporter",
  PROFESSIONAL_ATHLETE: "Professional Athlete",
  ARTIST: "Artist",
  RELIGIOUS: "Religious",
  POLICE_AND_MILITARY: "Police / Military",
  DIPLOMATIC: "Diplomat",
  CIVIL_SERVANT_A1: "Public Administration - Civil Servant A1",
  CIVIL_SERVANT_A2: "Public Administration - Civil Servant A2",
  CIVIL_SERVANT_B: "Public Administration - Civil Servant B",
  CIVIL_SERVANT_C1: "Public Administration - Civil Servant C1",
  CIVIL_SERVANT_C2: "Public Administration - Civil Servant C2",
  CIVIL_SERVANT_E: "Public Administration - Civil Servant E",
  CIVIL_SERVANT_G1: "Public Administration - Civil Servant G1",
  CIVIL_SERVANT_G2: "Public Administration - Civil Servant G2",
  CIVIL_SERVANT_G3: "Public Administration - Civil Servant G3",
  CIVIL_SERVANT_G4: "Public Administration - Civil Servant G4",
  CIVIL_SERVANT_G5: "Public Administration - Civil Servant G5",
  AGRICULTURE_LIVESTOCK_FISHERIES: "Agriculture / Livestock / Fisheries",
  MINING_METALLURGY: "Mining / Metallurgy",
  GROCERY: "Grocery",
  TEXTILE_MANUFACTURE: "Textile Manufacturing",
  WOOD: "Wood Industry",
  GRAPHICS_ARTS_EDITION: "Graphic Arts / Publishing",
  CHEMISTRY: "Chemistry",
  MECHANICAL_MACHINERY: "Mechanical Machinery",
  ENERGY_DISTRIBUTION_ELECTRICAL_SUPPLY: "Energy / Electrical Supply",
  OTHER_UNQUALIFIED_SERVICES: "Other Unqualified Services",
  OTHER_QUALIFIED_SERVICES: "Other Qualified Services",
  BUILDING: "Construction",
  COMMERCE: "Commerce",
  PEDDLING: "Peddling",
  TRANSPORT_TERRESTRIAL_MESSAGING: "Land Transport / Messaging",
  OTHER_TRANSPORTS: "Other Transport Services",
  TAXI_DRIVER: "Taxi / Chauffeur Service",
  TELECOMMUNICATIONS_MAIL: "Telecommunications / Mail",
  BARS_SERVICES: "Bar / Hospitality Services",
  I_PLUS_D: "Research and Development",
  ADVOCACY_JOURNALISM: "Journalism / Advocacy",
  SECURE_FINANCIAL_SERVICES: "Secure Financial Services",
  FINANCIAL_SERVICES_INSURANCE: "Insurance",
  IT_CONSULTANT_AUDITOR: "IT / Consulting / Auditing",
  ARCHITECTURE_ENGINEERING: "Architecture / Engineering",
  VETERINARY_MEDICINE: "Medicine / Veterinary",
  PUBLIC_ADMINISTRATIONS: "Public Administration",
  ARMED_FORCES: "Armed Forces",
  EDUCATION: "Education",
  HOLIDAY_SHOWS_SHOWS: "Holiday / Entertainment Shows",
  SPORTS: "Sports",
  INDIVIDUAL_NET_INCOME: "Net amount previous fiscal year",
  INDIVIDUAL_FINANCIAL_DATA_DATE: "Year of the previous fiscal year",
  SELF_EMPLOYED_DATA: "Self-employed data",
  NON_VALID_CIF: "Invalid CIF",
  COMPANY_CIF: "Company CIF",

  // Validate or Edit Income
  PROVIDE_MAIN_INCOME_SOURCE: "Indicate your main source of income",
  INCOME_AMOUNT_GREATER_THAN_VALUE:
    "The value of your income should be greater than $v1€",
  NET_INCOME_TYPE: "Type of income",
  MONTHLY_INCOME: "Monthly net income",
  NUMBER_OF_PAYMENTS_PER_YEAR: "Number of annual payments",
  BANK_ACCOUNT_IBAN: "Direct debit account IBAN",
  BANK_ACCOUNT_IBAN_WITHIN_SEPA: "Direct debit account IBAN within Eurozone",
  BANK_ACCOUNT_USAGE_REMINDER:
    "This bank account will only be used to direct debit the loan installments",
  BANK_ACCOUNT_USAGE_REMINDER_FOR_CARD:
    "This account will be used to charge the receipts of your financing",
  BANK_ACCOUNT_USAGE_REMINDER_FOR_INTERNATIONAL_STUDENT:
    "This bank account must belong to an entity within the Eurozone (SEPA) and will only be used to direct debit the loan installments.",
  INVALID_IBAN_USAGE_REMINDER:
    "The entered account number is not a valid IBAN. Please enter a valid IBAN or choose an alternative payment method for fee payment.",
  IBAN_NOT_IN_SEPA_LIST_USAGE_REMINDER:
    "The entered account number does not correspond to a Eurozone (SEPA) entity. Please enter a valid IBAN or choose an alternative payment method for fee payment.",
  DIRECT_DEBIT_ACCOUNT: "Direct debit account",
  SALARY: "Salary",
  PENSION: "Pension",
  UNEMPLOYMENT_BENEFIT: "Unemployment benefit",
  OTHER_PAYMENT: "Other type of income",
  NO_INCOME_TITLE: "Sorry, we were not able to detect income in the account",
  NO_INCOME_BODY:
    "Don't worry, you can manually input information about your income.",
  NO_INCOME_BODY_DOC:
    "You will need to upload documentation that proves your income at the end of the process.",
  IBAN_NOT_IN_SEPA_LIST: "The entered IBAN is not in the SEPA country list.",
  USE_DIFFERENT_PAYMENT_METHOD: "Use a different payment method",
  ALTERNATIVE_PAYMENT_METHOD: "Alternative payment method",
  INFORMATION: "Information",
  SELECT_THIS_PAYMENT_METHOD: "Select this method",
  DO_NOT_HAVE_BANK_ACCOUNT_NUMBER_WITHIN_THE_SEPA_ZONE:
    "I do not have a bank account number within the Eurozone (SEPA)",
  ALTERNATIVE_PAYMENT_METHOD_EXPLANATION: (
    <>
      <p>
        If you don't have an IBAN within the SEPA zone, payments can be made
        through Flywire, the platform that allows for global payments.
      </p>
      <p>
        If you choose this method, before each instalment's due date, you will
        receive an email with instructions to complete the payment quickly and
        securely.
      </p>
    </>
  ),
  // Adjust Loan
  GOOD: "Well done",
  YOUR_LOAN_RESULT: "You're almost done!",
  ADJUST_YOUR_QUOTA: "Adjust the quota you want to pay",
  LOAN_DURATION: "Duration",
  LOAN_TOTAL: "Asset amount",
  CREDIT_LINE_TOTAL: "Credit line amount",
  TIN: "NIR",
  TAE: "APR",
  TOTAL_TO_PAY: "Total to pay",
  GRACE_PERIOD: "Grace period",
  SETUP_FEE: "Setup fee",
  APPLY_FOR_LOAN: "Apply",
  EURO_PER_MONTH: "$v1 €/month",
  MONEY_PER_MONTH: "$v1/month",
  X_MONTHS: "$v1 months",
  CONDITIONS_MULTI_PRICING_MESSAGE:
    "The amount of the installments may vary depending on the financial institution. We show you the best available quote as a reference, and before paying you can review the final conditions.",
  APPLY_FOR_LOAN_BODY:
    "The application will only be binding when you sign the contract in case of approval.",
  DATA_TREATMENT: "Data treatment",
  INFORMATION_ABOUT_RISK_ANALYSIS:
    "Information about the processing of your data to analyze your creditworthiness",
  AGREE_CONDITIONS: "I agree to the conditions",
  NEED_TO_CHOOSE_TO_CONTINUE:
    "To be able to continue, it is necessary to choose 'Yes' ✓ or 'No' ✗ in all available options.",
  PLEASE_WAIT_APPLICATION:
    "Please wait a moment, we are processing your application. This process may take up to 1 minute.",
  TAKING_A_BIT_LONGER: "It's almost done, please be patient",
  ADD_CO_BORROWER: "Add co-borrower",
  ADD_GUARANTOR: "Add guarantor",
  CO_BORROWER_PERSONAL_DATA: "Co-borrower personal data",
  CO_BORROWER_CONTACT_DATA: "Co-borrower contact data",
  CO_BORROWER_FIRST_NAME: "Co-borrower first name",
  CO_BORROWER_LAST_NAME: "Co-borrower last name",
  CO_BORROWER_DOCUMENT_NUMBER: "Co-borrower ID number",
  CO_BORROWER_EMAIL: "Co-borrower email",
  CO_BORROWER_PHONE_NUMBER: "Co-borrower phone number",
  GUARANTOR_PERSONAL_DATA: "Guarantor personal data",
  GUARANTOR_CONTACT_DATA: "Guarantor contact data",
  GUARANTOR_FIRST_NAME: "Guarantor first name",
  GUARANTOR_LAST_NAME: "Guarantor last name",
  GUARANTOR_DOCUMENT_NUMBER: "Guarantor ID number",
  GUARANTOR_EMAIL: "Guarantor email",
  GUARANTOR_PHONE_NUMBER: "Guarantor phone number",
  CO_BORROWER_ADDITION_INFO_MESSAGE_BODY:
    "We will send an SMS and email to the co-borrower with a link to carry out this same application process with their information.",
  GUARANTOR_ADDITION_INFO_MESSAGE_BODY:
    "We will send an SMS and email to the guarantor with a link to carry out this same application process with their information.",
  CO_BORROWER_RELATION: "Co-borrower relationship",
  GUARANTOR_RELATION: "Guarantor relationship",
  CO_BORROWER_GUARANTOR_ADDED_CORRECTLY:
    "Co-borrower/guarantor added correctly",
  GUARANTOR_ADDED_CORRECTLY: "Guarantor added correctly",
  borrower: "Borrower",
  co_borrower: "Co-borrower",
  guarantor: "Guarantor",
  PARTICIPANT_ROLE: "Co-borrower type",
  SPOUSE: "Spouse",
  SIBLING: "Sibling",
  PARENT: "Parent",
  SON: "Child",
  REST_OF_FAMILY: "Other family member",
  PLEASE_SELECT_DOWN_PAYMENT_AND_TERM: "Please select down payment and term",
  FROM_X_TO_Y_MONTHS: "From $v1 to $v2 months",
  FROM_X_TO_Y_YEARS: "From $v1 to $v2 years",
  FROM_X_TO_Y: "From $v1 to $v2",
  INVALID_TERM_VALUE: "Term not included in fare",
  TERM_MUST_BE_BETWEEN_X_AND_Y: "Must be between $v1 and $v2 months",
  MONTHS: "Months",
  YEARS: "Years",
  UP_TO: "Up to",
  TERM: "Term",
  DOWN_PAYMENT: "Down payment",
  VEHICLE_PRICE_WITH_TAX: "Vehicle price (incl. taxes)",
  APPLICATION_AMOUNT: "Amount to finance",
  TOTAL_PRICE_IN_SPLIT_PAYMENT: "Total amount to pay",
  FEE_AMOUNT_AND_PAYMENT_PROTECTION_INSURANCE_MONTHLY_PERCENTAGE:
    "*Includes $v1 financed opening fee and $v2 monthly payment protection insurance.",
  INCLUDES_UPFRONT_SETUP_FEE_AMOUNT: "*Includes $v1 upfront setup fee.",
  INCLUDES_FINANCED_SETUP_FEE_AMOUNT:
    "*Includes $v1 financed setup fee per month.",
  DOWN_PAYMENT_OVER: "Down payment over",
  DOWN_PAYMENT_UNDER: "Down payment under",
  INVALID_VALUE: "Invalid down payment",
  AUTO_ADJUSTED_TERM: "Maximum term adjusted to the age of the vehicle.",
  DOWNPAYMENT_NOT_ALLOWED: "The adjustment of the down payment is not allowed",
  LOADING_LOAN_CONDITIONS: "Loading loan conditions...",
  CALCULATING_CONDITIONS: "Calculating conditions...",

  // Form input help
  HELP: "Help",
  CLOSE: "Close",
  REQUIRED_FIELD: "Required",
  INVALID: "Invalid",
  RECOMMENDED_FIELD: "Recommended",
  PLEASE_COMPLETE_THIS_FIELD_CORRECTLY: "Please complete this field correctly",
  INVALID_FORMAT: "Invalid format",
  PLEASE_CHECK_INPUT_DATA: "Please check input data.",
  NOT_VALID_IBAN_FORMAT: "Invalid IBAN format",
  INVALID_SPANISH_IBAN: "Please enter a Spanish IBAN.",
  // Documentation
  DOCUMENTATION: "Documentation",
  DOCUMENTATION_UPLOAD: "Please upload the following documents",
  DOCUMENTATION_SYNC:
    "Please wait, updating documentation. This process may take a few seconds.",
  PENDING_DOCUMENTATION: "$v1 documents pending",
  VALIDATE_DOCUMENTATION: "Upload documents",
  VALIDATE_DOCUMENTATION_WARNING: "Remember to click on 'Upload documents'!",
  FILES_WITH_INCIDENCE_WARNING:
    "Oops! The document is invalid. Please add it again and click on 'Done'",
  ADD_FILE_CONFIRMATION: "Add new document",
  ADD_FILE_DESCRIPTION: "The following document will be uploaded",
  ADD_FILE_ACTION: "Add document",
  UPLOAD_FILE_ACTION_ASSERTION:
    "Please confirm the documentation before uploading it",
  INCIDENCE: "Document incidence",
  DONE: "Done",
  UPLOAD_FILE_ACTION: "Upload",
  UPLOAD_FILE_ACTION_MULTIPLE: "Upload $v1 documents",
  UPLOAD_FILE_ACTION_SINGLE: "Upload 1 document",
  UPLOADING_FILE_ACTION_MULTIPLE: "Uploading $v1 documents...",
  UPLOADING_FILE_ACTION_SINGLE: "Uploading 1 document...",
  BANK_ACCOUNT_OWNERSHIP_DOCUMENTATION: "Certificate of bank account ownership",
  DOCUMENT_REQUIRED_DESCRIPTION_BANK_ACCOUNT_OWNERSHIP_DOCUMENTATION:
    "Certificate of bank account ownership, receipt or screenshot of your online banking account (owner's IBAN, name and document number must be visible)",
  PAYROLL: "Last available payslip",
  DOCUMENT_REQUIRED_DESCRIPTION_PAYROLL: "Last available payslip",
  PAYROLL_TWO_PREVIOUS_MONTHS: "Second last available payslip",
  PERSONAL_INCOME_TAX: "Annual personal income tax (Model 100)",
  BIOMETRICS_DOCUMENT: "Biometric document from the KYC process",

  DOCUMENT_ID_FRONT: "Front side of the DNI / NIE",
  DOCUMENT_ID_BACK: "Back side of the DNI / NIE",
  DOCUMENT_ID_FRONT_DNI: "Front side of the DNI",
  DOCUMENT_ID_BACK_DNI: "Back side of the DNI",
  DOCUMENT_ID_FRONT_NIE: "Front side of the NIE",
  DOCUMENT_ID_BACK_NIE: "Back side of the NIE",
  UPLOAD_DOCUMENT_ID_PICTURES: "Upload identity documents",
  LOAD_DOCUMENT_ID_FRONT_NIE: "Load front side of the NIE",
  LOAD_DOCUMENT_ID_BACK_NIE: "Load back side of the NIE",
  LOAD_DOCUMENT_ID_FRONT_DNI: "Load front side of the DNI",
  LOAD_DOCUMENT_ID_BACK_DNI: "Load back side of the DNI",

  ENROLLMENT_OF_THE_STUDENT: "Enrollment certificate",
  DOCUMENT_REQUIRED_DESCRIPTION_ENROLLMENT_OF_THE_STUDENT:
    "Course enrollment certificate",
  DATE_FORMAT_YEAR: "Year",
  DATE_FORMAT_MONTH_YEAR: "Month, year",
  DATE_FORMAT_DAY_MONTH_YEAR: "Day, month, year",
  DATE_LOCALE: enLocale,
  SAVE_CHANGES_PROMT: "Do you want to save the changes made?",
  SAVE_CHANGES: "Save changes",
  DISCARD_CHANGES: "Discard changes",
  DOCUMENT_ID_NUMBER_DIFFERENT_THAN_INVITATION:
    "Document number does not correspond to the financing proposal",

  //CARD
  FIXED_INSTALMENT: (
    <>
      Deferred payment
      <br />
      (revolving)
    </>
  ),
  END_OF_MONTH: "End of month",
  FIXED_INSTALMENT_DESCRIPTION: (
    <>
      With the deferred payment option (revolving), you pay in installments,
      with the monthly instalment you choose, the credit you are using with the
      agreed interest.
      <br />
      You can choose the instalment amount you want to pay monthly, always
      starting from a minimum instalment that we set to avoid the debt being
      prolonged over time.
    </>
  ),
  END_OF_MONTH_DESCRIPTION: (
    <>
      With the End of month payment option, the client pays at the end of the
      month, without interests, all the credit used during the month. The option
      selected does not affect this purchase or future instalment payments.
      <br />
      You can change the option when you want.
    </>
  ),
  SELECT_PAYMENT_MODE: "Select your payment mode",
  SELECT_PAYMENT_MODE_SUBTITLE:
    "Choose how you want to pay your next purchases",
  CARD_SHIPPING_DESCRIPTION:
    "When you finish the request, we will send you the card and you can use it for any payment.",
  FIXED_INSTALMENT_INFORMATION_DESCRIPTION:
    "Remember that from now on a new formula with a corrector factor is applied to calculate your minimum instalment and that the amount of this minimum instalment can change from one month to another since it takes into account the total amount of your spent. The objective of this new formula is to ensure that with this minimum instalment the debt payment is not prolonged excessively and you do not pay interest indefinitely.",
  OTHER_AMOUNT: "Other amount",
  REPAYMENT_AMOUNT_GREATER_THAN_MAX: "The maximum amount is $v1€",
  MONTHLY_AMOUNT: "Monthly amount:",
  CARD_INSURANCE: "Payment protection insurance",
  CARD_INSURANCE_SUBTITLE:
    "Activate your payment protection insurance voluntarily",
  SEE_EXAMPLE: "See example",
  WHAT_IS_THE_INSURANCE_COST: "What is the insurance cost?",
  CARD_INSURANCE_EXAMPLE_CAPTION_TOP:
    "The insurance cost will be $v1 for every 100€ of debt.",
  CARD_INSURANCE_EXAMPLE_CAPTION_BOTTOM:
    "Example of a 300€ purchase in 3 instalments of 100€",
  CARD_INSURANCE_EXAMPLE_COL_MONTH: "Month",
  CARD_INSURANCE_EXAMPLE_COL_DEBT: "Debt",
  CARD_INSURANCE_EXAMPLE_COL_INSURANCE: "Insurance payment?",
  CARD_INSURANCE_EXAMPLE_COL_TOTAL: "Total payment?",
  CARD_INSURANCE_EXAMPLE_MONTH_1: "Month 1",
  CARD_INSURANCE_EXAMPLE_MONTH_2: "Month 2",
  CARD_INSURANCE_EXAMPLE_MONTH_3: "Month 3",
  CARD_INSURANCE_DESCRIPTION_START: (
    <>
      Being protected with the Payment Protection Insurance means a minimum
      amount per month and, in return, you benefit from the maximum peace of
      mind.
    </>
  ),
  CARD_INSURANCE_DESCRIPTION_END: (
    <>
      This insurance is voluntary and not taking it out does not affect the
      economic conditions of the financing product.
    </>
  ),
  CARD_INSURANCE_CONDITIONS:
    "You will only have to pay $v1 per month for each 100€ of debt.",
  IMPORTANT_INFORMATION: "Important information!",
  CARD_DOCUMENTATION_VALIDATION_INSTRUCTIONS: (
    <>
      After digitally signing the contract, go to the IKEA financing counter and
      show the QR code you will receive by email to an agent to complete the
      validation of your documentation.
    </>
  ),
  UNEMPLOYMENT_PROTECTION_INSURANCE: "Unemployment protection",
  UNEMPLOYMENT_PROTECTION_INSURANCE_DESCRIPTION:
    "Indemnization with 10% of the available balance until the date of dismissal, and with a maximum of 6 consecutive monthly payments with a maximum of 400€ per month.",
  PAYMENT_PROTECTION_INSURANCE: "Payment guarantee",
  [`PAYMENT_PROTECTION_INSURANCE_DESCRIPTION_${INSURANCE_PACK.SENIOR}`]:
    "Coverage of the pending credit liquidation in case of death of the cardholder.",
  [`PAYMENT_PROTECTION_INSURANCE_DESCRIPTION_${INSURANCE_PACK.PROTECTION_PLUS}`]:
    "Coverage of the pending credit liquidation in case of severe disability, death cancer or heart attack of the cardholder.",
  LEGAL_TERMS_DESCRIPTION: (
    <>
      <p>
        By indicating "I want to protect my future purchases", I declare, for
        the purposes of this Application, that as of today I do not suffer from
        any serious illness known to me, nor am I in a situation of permanent
        disability in any of its degrees (total, partial, absolute, or major
        disability), or temporary disability that could lead to permanent
        disability, nor am I in a situation of unemployment, in the event that I
        take out unemployment or temporary disability coverage.
      </p>
      <p>
        Information subject to the conditions of the policy contained in the
        General Terms and Conditions of contracting to which I have had access
        and which I have read. CaixaBank Payments & Consumer, E.F.C., E.P.,
        S.A.U. (CaixaBank Payments & Consumer), with CIF A-08980153 and
        registered office at Avenida de Manoteras nº 20, Edificio París, 28050
        Madrid, acts in the arrangement of the insurance as a tied bancassurance
        operator of VidaCaixa S.A. de Seguros y Reaseguros and SegurCaixa
        Adeslas S.A. de Seguros y Reaseguros, registered in the special
        administrative register of insurance intermediaries of the Directorate
        General of Insurance and Pension Funds, under number OV0077, as may be
        verified before it, with registered office at Paseo de la Castellana
        259C, Torre de Cristal building, Madrid (28046), telephone +34 91 339 70
        00, www.dgsfp.mineco.es, has arranged civil liability insurance and has
        financial capacity in accordance with legal requirements. The parent
        company of CaixaBank Payments & Consumer holds a stake of more than 10%
        of the share capital of VidaCaixa and SegurCaixa.
      </p>
    </>
  ),
  CONTINUE_WITH_INSURANCE: "Continue with insurance",
  CONTINUE_WITHOUT_INSURANCE: "Continue without insurance",
  DO_YOU_WANT_TO_HAVE_INSURANCE: "Do you want to have insurance?",
  PLEASE_SELECT_IF_YOU_WANT_TO_HAVE_INSURANCE:
    "Please select if you want to have insurance",
  LEGAL_TERMS: "Legal information",
  LEGAL_TERMS_SUBTITLE: "About payment protection insurance",
  SEE_LEGAL_TERMS: "See legal information",
  VALIDATION_LINK: "Data validation",
  VALIDATION_LINK_SUBTITLE: "Scan the QR code to validate your data",
  VALIDATION_LINK_QR_CODE_DESCRIPTION:
    "As the final step of the process, you must go to the IKEA customer service, where an agent will validate the information and proceed to the issuance of your card",
  VALIDATION_LINK_QR_CODE_DESCRIPTION_1: (
    <>
      <p style={{ fontSize: "1.25rem", fontWeight: "700" }}>Congratulations!</p>
      <p>
        <strong>
          You have successfully completed the request for your VISA IKEA card.
        </strong>
      </p>
      <p>
        To make today's purchase easier, go to any planning area.{" "}
        <strong>
          You have <u>up to 3 hours to pick up your card!</u>
        </strong>
      </p>
      <p>
        Show the following <strong>code to speed up the process:</strong>
      </p>
    </>
  ),
  VALIDATION_LINK_QR_CODE_DESCRIPTION_2: (
    <>
      <p>
        <strong>
          We recommend you go as soon as possible, but if you think you will not
          be able to pick it up in the next 3 hours, don't worry! You will
          receive your card at your home within 7 to 10 days.
        </strong>
      </p>
      <p>
        Remember that you can activate and enjoy all the functionalities of your
        card in our app: from enrolling it to managing the installment of your
        purchases.
      </p>
    </>
  ),
  PRODUCT: "Product",
  PAYMENT_MODE: "Payment mode",
  FIXED_INSTALMENT_AMOUNT: "Deferred payment amount",
  SELECT_FIXED_INSTALMENT_AMOUNT: "Select an amount:",
  FIXED_INSTALMENT_AMOUNT_SUBTITLE:
    "Choose the amount of money you want to pay each month",
  PRODUCT_DESCRIPTION: "Product description",
  [PaymentMode.END_OF_MONTH]: "End of month",
  [PaymentMode.FIXED_INSTALLMENT]: "Deferred payment",
  CREDIT_LIMIT_NOTICE: "Granted credit limit: $v1",

  ACTION_GENERIC_ERROR: "Oops! An error has occurred.",
  ACTION_GENERIC_INFO: "The action has been performed correctly.",
  ACTION_GENERIC_SUCCESS: "The action has been performed correctly.",
  ACTION_GENERIC_WARNING: "Please review the data.",
  ACTION_GENERIC_ERROR_TITLE: "The action could not be performed.",
  // ACTIONS MESSAGES
  ACTION_REQUEST_GET_SIGNATURE_URL_BY_LOAN_ID_REJECTED: (
    <div>
      <p>
        We are currently unable to connect to the bank to continue with the
        signing process.
      </p>
      <br></br>
      <span>Please:</span>
      <p>
        <ul>
          <li>
            Try connecting to the bank again by clicking on the button below.
          </li>
          <li>If the error persists, try again later.</li>
        </ul>
      </p>
    </div>
  ),
  //Exceptions
  EXCEPTION_BorrowerOnboardingDetailsReferenceIsNotNullException:
    "This request already has an assigned co-borrower",
  [ACTION_EXCEPTIONS.INVALID_DOCUMENT_NUMBER_EXCEPTION]: (
    <>
      <p>It seems that the identity document number is not valid.</p>
      <p>Please, retry the video identification.</p>
    </>
  ),
  [ACTION_EXCEPTIONS.INVALID_ELECTRONIC_ID_INFORMATION_EXCEPTION]: (
    <>
      <p>It seems that some information has not been registered correctly.</p>
      <p>Please, retry the video identification.</p>
    </>
  ),
  [ACTION_EXCEPTIONS.INVALID_ISO3166_EXCEPTION]: (
    <>
      <p>It seems that your nationality has not been correctly registered.</p>
      <p>Please, retry the video identification.</p>
    </>
  ),
  [ACTION_EXCEPTIONS.INVALID_DATE_EXCEPTION]: (
    <>
      <p>It seems that the date is invalid.</p>
      <p>Please, retry the video identification.</p>
    </>
  ),
  RETRY_VIDEO_ID_BUTTON: "Retry video identification",

  // SignatureEmbeddedSAML
  SIGN: "Review and sign contract",
  FOLLOW_INSTRUCTIONS_TO_SIGN: "Follow instructions to sign contract",
  PROCEED_TO_DIGITAL_SIGNATURE: "Review and digital signature",
  CONTRACT_SIGNATURE: "Contract signature",
  CONNECTING: "Connecting...",
  RETRIEVING_DATA: "Retrieving data...",
  DIGITAL_SIGNATURE_INFO:
    "By continuing, you will start the digital signature process",
  REVIEW_SIGNATURE_INFO:
    "Don't worry, you can review everything before signing",
  SIGNATURE_URL_REQUEST_ERROR:
    "We are currently unable to connect to the bank to continue with the signing process.",
  TRY_AGAIN_PRESS_BUTTON:
    "Try connecting to the bank again by clicking on the button below.",
  TRY_AGAIN_LATER: "If the error persists, try again later.",
  BEFORE_PROCEEDING_YOU_NEED_TO_ANSWER:
    "Before signing the contract, you need to answer the following questions.",
  FINALIZE: "Continue",
  CLICK_FINALIZE_AFTER_SIGNATURE:
    'When the signature is complete, click "Continue"',
  ACTION_LOAN_NOT_SIGNED: (
    <>
      We have not been able to validate that the contract signature has been
      completed.
      <br />
      <p style={{ fontWeight: "normal" }}>
        It is necessary to confirm that the signature process has been finalized
        to continue with the request. Once the process is completed, click on
        "Continue".
      </p>
    </>
  ),
  // Misc
  POWERED_BY: "Powered by",
  //payment
  BACK: "Back",
  PAYMENT: "Payment",
  YOUR_INSTALMENT: "Your Instalment",
  INSTALMENT_DETAIL: "Instalment Detail",
  REVIEW_PAYMENT_PLAN: "Review Payment Plan",
  YOUR_PAYMENT_PLAN: "Your Payment Plan",
  PAYMENT_PLAN_STATUS: "Payment Plan Status",
  PAY_QUOTA_NUMBER: "Pay Quota $v1",
  TOTAL_PAID_AMOUNT: "Total Paid Amount",
  TOTAL_REMAINING_AMOUNT: "Total Remaining Amount",
  INSTALMENTS_PAID: "Instalments Paid",
  OF: "of",
  PAY_AMOUNT: "Pay $v1",
  QUOTA_NUMBER: "Quota Number",
  QUOTA_AMOUNT: "Quota Amount",
  DUE_DATE: "Due Date",
  PAYMENT_STATUS: "Payment Status",
  [`PAYMENT_PLAN_LINE_STATUS_${PaymentPlanLineStatus.PENDING}`]: "Pending",
  [`PAYMENT_PLAN_LINE_STATUS_${PaymentPlanLineStatus.PAID}`]: "Paid",
  [`PAYMENT_PLAN_LINE_STATUS_${PaymentPlanLineStatus.CANCELLED}`]: "Cancelled",
  [`PAYMENT_PLAN_LINE_STATUS_${PaymentPlanLineStatus.PROCESSING}`]:
    "Payment started",
  PAYMENT_PLAN_LINE_PAID_TITLE: "Paid Instalment",
  PAYMENT_PLAN_LINE_PAID_TEXT:
    "This instalment has been paid successfully. There are no pending actions at this moment. You can close this tab.",
  PAYMENT_PLAN_LINE_CANCELLED_TITLE: "Cancelled Instalment",
  PAYMENT_PLAN_LINE_CANCELLED_TEXT:
    "This instalment has been cancelled. There are no pending actions at this moment. You can close this tab.",
  PAYMENT_RESULT_SUCCESS_TITLE: "All Set!",
  PAYMENT_RESULT_SUCCESS_TEXT:
    "The payment for the instalment has been processed successfully. You can close this tab.",
  PAYMENT_RESULT_ERROR_TITLE: "An issue has occurred",
  PAYMENT_RESULT_ERROR_TEXT:
    "The payment for this instalment could not be processed. Please try again by clicking the following button.",
  FLYWIRE_PAYMENT_INSTALMENT_DETAIL:
    "Click to make the payment for the current instalment and remember that you will receive an email each month to make the payment for the corresponding instalment using the payment method that suits you best.",
  PAYMENT_PLAN_LINE_PENDING_TITLE: "Unpaid instalment",
  PAYMENT_PLAN_LINE_PENDING_TEXT:
    "There was an issue processing the payment for this instalment. Please, try again by clicking the button.",
  PAYMENT_RESULT_PAID_TITLE: "Payment Successful",
  PAYMENT_RESULT_PENDING_TITLE: "Oops!",
  PAYMENT_RESULT_PENDING_TEXT:
    "It seems that there was an error while trying to process the payment for the instalment. Please, click the button to retry the payment for the instalment.",
  QUOTA_WITH_NUMBER: "Quota $v1",
  QUOTA_AND_SETUP_FEE_WITH_NUMBER: "Quota $v1 + Setup fee",
  PREAPPROVED_DOCUMENTS_UPLOADED_SIGNATURE_NOT_AVAILABLE:
    "Documentation uploaded successfully.",
  PAY_WITH_FLYWIRE: "Pay with Flywire",
  EMAIL_SHOULD_NOT_BE_THE_SAME_AS: "Email cannot be the same as $v1",
  PHONE_NUMBER_SHOULD_NOT_BE_THE_SAME_AS:
    "Phone number cannot be the same as $v1",
  SHOW_MORE: "Show more",
  SHOW_LESS: "Hide",
  PAYMENT_PLAN_LINE_PROCESSING_TITLE: "Payment initiated",
  PAYMENT_PLAN_LINE_PROCESSING_TEXT:
    "The payment for the instalment has been initiated and is pending. If you have received instructions for making the payment, please complete them and return to this link. If not, the system may be taking a bit longer than usual. Nevertheless, once the payment has been processed successfully, the instalment will appear as paid. If there is any issue, you can try the payment again.",
  PAYMENT_RESULT_PROCESSING_TITLE: "Payment initiated",
  PAYMENT_RESULT_PROCESSING_TEXT:
    "The payment for the instalment has been initiated and is pending. If you have received instructions for making the payment, please complete them and return to this link. If not, the system may be taking a bit longer than usual. Nevertheless, once the payment has been processed successfully, the instalment will appear as paid. If there is any issue, you can try the payment again.",

  //ARIA LABELS
  DNI_ARIA_LABEL: "Spanish identity document",
  NIE_UE_ARIA_LABEL: "European identity document",
  NIE_NON_UE_ARIA_LABEL: "Identity document outside the European Union",
  PASSPORT_ARIA_LABEL: "Passport",

  //LENDER SELECTION
  INSTALMENT_AMOUNT: "Instalment amount",
  SELECT: "Select",
  SELECT_LENDER: "Financial institutions",
  SELECT_LENDER_SUBTITLE:
    "Select with which financial institution you want to continue with the process",
  SELECT_ONE_OF_THE_FOLLOWING_OPTIONS: "Select one of the following options:",
  SELECT_LENDER_DESCRIPTION:
    "By selecting the financial institution with which you want to continue with the process, the conditions you can see on the card of each of the options to your financing proposal will be applied.",
  ANSWERED_YES: "Accepted",
  ANSWERED_NO: "Denied",
  IN_STUDY: "In study",
  FINANCING_PROVIDED_BY: "Financing provided by",
  TO_PAY: "To pay",
  CONTINUE_WITH: "Continue with",
  [LenderName.CAIXABANK]: "CaixaBank",
  [LenderName.CETELEM]: "Cetelem",
  [LenderName.BS]: "Banco Sabadell",
  [LenderName.BBVA]: "BBVA",
  [LenderName.SOYOU]: "Sofinco",
  [LenderName.ABANCA]: "Abanca",
  [LenderName.SANTANDER]: "Santander",
  [LenderName.SANTANDER_CONSUMER]: "Santander Consumer",

  LOAN_CONDITIONS: "Conditions",
  SECTION_LOAN_CONDITIONS: "Conditions",
  END_SECTION_LOAN_CONDITIONS: "Conditions selected",
  AVAILABLE_LENDERS: "Lenders available in the selected term",
  LENDERS: "Available lenders",
  QUOTA_SELECTION: "Quota selection",
  SECTION_LOAN_CONDITIONS_END: "Conditions selected",

  //Call Me back panel
  CAN_WE_HELP_YOU: "Let's talk?",
  FULL_NAME: "Full name",
  PREFERRED_TIME_SLOT: "Preferred time slot",
  DO_YOU_NEED_HELP_WITH_YOUR_APPLICATION:
    "Do you need help with your application?",
  YOU_CAN_CALL_US_OR_WE_CALL_YOU_BACK:
    "You can call us at 931 227 103 or leave us your data and we will call you back.",
  REQUEST_CALL: "Request call",
  PHONE_NUMBER: "Phone number",
  ASAP: "As soon as possible",
  MORNING: "Morning (9h-12h)",
  NOON: "Noon (12h-15h)",
  AFTERNOON: "Afternoon (15h-18h)",
  EVENING: "Evening (18h-21h)",
  THANK_YOU_FOR_COMMUNICATING_WITH_US: "Thank you for communicating with us!",
  WE_WILL_CALL_YOU_BACK_AT_THE_SPECIFIED_TIME:
    "We will call you back at the specified time.",
  CALL_ME_BACK_DATA_TREATMENT: (
    <>
      <h3>Information on data processing:</h3>
      <p style={{ marginBlock: "0.3rem" }}>
        <strong>Purpose:</strong> To manage your request for financing
        assistance and to contact you by phone as requested.
      </p>
      <p style={{ marginBlock: "0.3rem" }}>
        <strong>Legal basis:</strong> Application of pre-contractual measures
        (Art. 6.1.b GDPR).
      </p>
      <p style={{ marginBlock: "0.3rem" }}>
        <strong>Retention:</strong> We will retain your data while managing your
        request and for up to 15 days after the last interaction. Afterwards,
        the data will be blocked in order to address any potential claims in
        accordance with applicable regulations.
      </p>
      <p style={{ marginBlock: "0.3rem" }}>
        <strong>Rights:</strong> You may exercise your rights of access,
        rectification, erasure, objection, restriction and data portability by
        contacting{" "}
        <a href="mailto:ejerciciodederechos@caixabankpc.com" target="_blank">
          ejerciciodederechos@caixabankpc.com
        </a>{" "}
        indicating “Facilitea – Used Vehicle” as a reference and providing a
        copy of your identity document.
      </p>
      <p style={{ marginBlock: "0.3rem" }}>
        You can consult the full information on the processing of your data in
        our privacy policy{" "}
        <a
          href="https://www.caixabankpc.com/es/politica-de-privacidad"
          target="_blank"
        >
          https://www.caixabankpc.com/es/politica-de-privacidad
        </a>
      </p>
    </>
  ),
};
export default en;
